import { Dialog } from '@mui/material';
import { useEffect, useState } from 'react';
import type { BaseReactPlayerProps } from 'react-player/base';

import Button, {
  ButtonIconPosition,
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import { IconName } from '@/components/Icon/Icon';
import VideoPlayer from '@/components/VideoPlayer/VideoPlayer';
import { brandColors } from '@/styles/theme/values/brandColors';
import { getVideoAspectRatio } from '@/utils/vimeo';

import useStyles from './HighlightLightboxPlayer.styles';

interface HighlightLightboxVideoPlayerProps {
  videoLink: string;
  isOpen: boolean;
  onClose: () => void;
  backButtonText?: string;
}

const HighlightLightboxVideoPlayer = ({
  videoLink,
  isOpen,
  onClose,
  backButtonText,
}: HighlightLightboxVideoPlayerProps) => {
  const { classes } = useStyles();
  const [aspectRatio, setAspectRatio] = useState('16/9');
  const [widthRatio, heightRatio] = aspectRatio.split('/').map(Number);
  const [playerDimensions, setPlayerDimensions] = useState({
    width: 0,
    height: 0,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: Automated migration to Biome
  useEffect(() => {
    // calculating the exact iframe width/height based on provided aspect ratio.
    // this lets us capture clicks outside the iframe to dismiss modal and relatively center the back button
    const updateDimensions = () => {
      if (widthRatio > heightRatio) {
        let width = window.innerWidth;
        let height = (width * heightRatio) / widthRatio;

        // handles horizontal video for vertical screens
        if (height > window.innerHeight) {
          height = window.innerHeight;
          width = (height * widthRatio) / heightRatio;
        }

        setPlayerDimensions({ width, height });
      } else {
        let height = window.innerHeight;
        let width = (height * widthRatio) / heightRatio;

        // handles vertical video for horizontal screens
        if (width > window.innerWidth) {
          width = window.innerWidth;
          height = (width * heightRatio) / widthRatio;
        }

        setPlayerDimensions({ width, height });
      }
    };
    updateDimensions();

    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [aspectRatio]);

  const handleReady: BaseReactPlayerProps['onReady'] = async (player) => {
    const videoElement = player.getInternalPlayer();
    const aspectRatio = await getVideoAspectRatio(videoElement);

    setAspectRatio(aspectRatio || '16/9');
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      role='dialog'
      className={classes.dialog}
      PaperProps={{ className: classes.paper }}
    >
      <section
        data-testid='highlight-lightbox-player'
        className={classes.container}
      >
        <div className={classes.backButtonOverlay}>
          <Button
            label={backButtonText ? `Back to ${backButtonText}` : 'Back'}
            iconName={IconName.ARROW_LEFT}
            iconPosition={ButtonIconPosition.LEFT}
            variant={ButtonVariant.TERTIARY}
            size={ButtonSize.SM}
            isOnDark
            onClick={onClose}
          />
        </div>
        <VideoPlayer
          url={videoLink}
          width={playerDimensions.width}
          height={playerDimensions.height}
          onReady={handleReady}
          playing
          controls
          config={{
            playerOptions: { color: brandColors.chargeBlue.substring(1) },
          }}
        />
      </section>
    </Dialog>
  );
};

export default HighlightLightboxVideoPlayer;
